/** Display helpers. All formatting lives here so the UI never improvises. */

import { localeFor } from '@respiria/shared';

// The active locale is a module scalar kept in sync by useT() (which knows the
// effective language). Formatting reads it so months/numbers match the UI
// language without threading a locale through every call site. Defaults to
// Spanish until useT runs on the first render.
let activeLocale = 'es-ES';

/** Called by useT() whenever the effective language is known. */
export function setFormatLocale(lang: string): void {
  activeLocale = localeFor(lang);
}

/** The active BCP-47 locale, for inline Intl formatters outside this file. */
export function formatLocale(): string {
  return activeLocale;
}

export function formatMoney(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(activeLocale, {
    style: 'currency',
    currency,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** "12d 4h 32m" — the hero counter. Drops units that are zero at the front. */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/** Translator signature (from useT), passed in so lib/format stays UI-agnostic. */
type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Long form for milestones: "3 días", "2 semanas". Localised via `t`. */
export function formatDurationLong(totalSeconds: number, t: Translate): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const units: [number, string, string][] = [
    [31_536_000, 'dur.year', 'dur.years'],
    [2_592_000, 'dur.month', 'dur.months'],
    [604_800, 'dur.week', 'dur.weeks'],
    [86_400, 'dur.day', 'dur.days'],
    [3600, 'dur.hour', 'dur.hours'],
    [60, 'dur.minute', 'dur.minutes'],
  ];

  for (const [size, singularKey, pluralKey] of units) {
    if (seconds >= size) {
      const value = Math.floor(seconds / size);
      return t(value === 1 ? singularKey : pluralKey, { n: value });
    }
  }
  return t('dur.seconds');
}

/** "en 4 horas" / "hace 2 días". Localised via `t`. */
export function formatRelative(iso: string, t: Translate, now = new Date()): string {
  const diffSeconds = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  if (abs < 60) return diffSeconds >= 0 ? t('rel.now') : t('rel.justNow');
  const duration = formatDurationLong(abs, t);
  return diffSeconds > 0 ? t('rel.in', { duration }) : t('rel.ago', { duration });
}

export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(activeLocale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/**
 * Day and month, plus the year when it isn't the current one (issue #67). A
 * savings goal 856 days out read "cap al 21 de desembre" with no year, which
 * looks like this December instead of the one after next.
 */
export function formatDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(activeLocale, {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** Local calendar date (YYYY-MM-DD) for the browser's timezone. */
export function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
  } catch {
    return 'Europe/Madrid';
  }
}

/**
 * Value for `<input type="datetime-local">`, which expects local wall-clock
 * time with no zone — never an ISO UTC string.
 */
export function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** UUID for offline idempotency. crypto.randomUUID needs a secure context. */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
